import type {Dispatch, ThunkAction} from 'src/reducers';
import type {FilterColumns} from 'src/types/analytics-genesis';
import {
  discoverJobSearchWorkflowsTableColumnsQuery,
  discoverJobSearchChatbotTableColumnsQuery,
  discoverJobSearchNodesTableColumnsQuery,
} from 'src/utils/analytics-genesis';
import {fetching, key} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {thunkify as flow} from 'src/utils/thunks';


export const SET_DISCOVER_JOB_SEARCH_CURRENT_DASHBOARD_ID =
  'analytics-job-matching-analytics/set-current-dashboard';
export const RECEIVE_DISCOVER_JOB_SEARCH_WORKFLOWS_TABLE_COLUMNS =
  'analytics-job-matching-analytics/receive-workflows-table-columns';
export const RECEIVE_DISCOVER_JOB_SEARCH_NODES_TABLE_COLUMNS =
  'analytics-job-matching-analytics/receive-nodes-table-columns';
export const RECEIVE_DISCOVER_JOB_SEARCH_CHATBOT_TABLE_COLUMNS =
  'analytics-job-matching-analytics/receive-chatbot-table-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_DISCOVER_JOB_SEARCH_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

export type ReceiveWorkflowsTableColumnsAction = {
  type: typeof RECEIVE_DISCOVER_JOB_SEARCH_WORKFLOWS_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveNodesTableColumnsAction = {
  type: typeof RECEIVE_DISCOVER_JOB_SEARCH_NODES_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveChatbotTableColumnsAction = {
  type: typeof RECEIVE_DISCOVER_JOB_SEARCH_CHATBOT_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type AnalyticsDiscoverJobSearchActions =
  | SetCurrentDashboardIdAction
  | ReceiveWorkflowsTableColumnsAction
  | ReceiveNodesTableColumnsAction
  | ReceiveChatbotTableColumnsAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_DISCOVER_JOB_SEARCH_CURRENT_DASHBOARD_ID,
  payload,
});

export const receiveWorkflowsTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveWorkflowsTableColumnsAction => ({
  type: RECEIVE_DISCOVER_JOB_SEARCH_WORKFLOWS_TABLE_COLUMNS,
  payload,
});

export const receiveNodesTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveNodesTableColumnsAction => ({
  type: RECEIVE_DISCOVER_JOB_SEARCH_NODES_TABLE_COLUMNS,
  payload,
});

export const receiveChatbotTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveChatbotTableColumnsAction => ({
  type: RECEIVE_DISCOVER_JOB_SEARCH_CHATBOT_TABLE_COLUMNS,
  payload,
});

export const getWorkflowsTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getDiscoverJobSearchWorkflowsTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {columns: FilterColumns} = await dispatch(
      reduxApi.get(
        'analytics/job-matching-analytics/column-selection',
        discoverJobSearchWorkflowsTableColumnsQuery,
      ),
    );
    dispatch(receiveWorkflowsTableColumns(payload) as any);
  } catch {
    // Error fetching discover job search workflows table columns
  }
});

export const getNodesTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getDiscoverJobSearchNodesTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {columns: FilterColumns} = await dispatch(
      reduxApi.get(
        'analytics/job-matching-analytics/column-selection',
        discoverJobSearchNodesTableColumnsQuery,
      ),
    );
    dispatch(receiveNodesTableColumns(payload) as any);
  } catch {
    // Error fetching discover job search nodes table columns
  }
});

export const saveWorkflowsTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/job-matching-analytics/column-selection',
        columnData,
        discoverJobSearchWorkflowsTableColumnsQuery,
      ),
    ),
);

export const saveNodesTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/job-matching-analytics/column-selection',
        columnData,
        discoverJobSearchNodesTableColumnsQuery,
      ),
    ),
);

export const getChatbotTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getDiscoverJobSearchChatbotTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {columns: FilterColumns} = await dispatch(
      reduxApi.get(
        'analytics/job-matching-analytics/column-selection',
        discoverJobSearchChatbotTableColumnsQuery,
      ),
    );
    dispatch(receiveChatbotTableColumns(payload) as any);
  } catch {
    // Error fetching discover job search chatbot table columns
  }
});

export const saveChatbotTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/job-matching-analytics/column-selection',
        columnData,
        discoverJobSearchChatbotTableColumnsQuery,
      ),
    ),
);
